/**
 * Error Handling Utilities
 *
 * Common utilities for handling API errors in forms and components.
 */

import type { APIErrorException } from '../services/types/errors';

/**
 * Maps API validation error details to form field errors
 *
 * @param details - Error details from API response
 * @returns Record of field names to error messages
 */
export const mapValidationErrors = (
  details: Record<string, any> | undefined
): Record<string, string> => {
  if (!details) {
    return {};
  }

  const formErrors: Record<string, string> = {};
  Object.entries(details).forEach(([field, messages]) => {
    if (Array.isArray(messages)) {
      formErrors[field] = messages.join(', ');
    } else {
      formErrors[field] = String(messages);
    }
  });

  return formErrors;
};

/**
 * Standard error handler for form mutations
 *
 * Handles common error cases with appropriate toast messages
 * and form error mapping for validation errors.
 *
 * @param error - APIErrorException from mutation
 * @param setErrors - Form errors setter function
 * @param options - Optional custom error messages
 */
export const handleMutationError = (
  error: APIErrorException,
  setErrors: (errors: Record<string, string>) => void,
  options?: {
    validationTitle?: string;
    conflictTitle?: string;
    conflictDescription?: string;
    notFoundTitle?: string;
    notFoundDescription?: string;
    genericTitle?: string;
    genericDescription?: string;
  }
) => {
  // Handle validation errors (400)
  if (error.isValidationError()) {
    const details = error.getDetails();
    if (details) {
      setErrors(mapValidationErrors(details));
    }
    return {
      title: options?.validationTitle || 'Validation Error',
      description: error.error.message,
    };
  }

  // Handle conflict errors (409)
  if (error.isConflictError()) {
    return {
      title: options?.conflictTitle || 'Conflict Error',
      description: options?.conflictDescription || error.error.message,
    };
  }

  // Handle not found errors (404)
  if (error.isNotFoundError()) {
    return {
      title: options?.notFoundTitle || 'Not Found',
      description: options?.notFoundDescription || error.error.message,
    };
  }

  // Handle other errors
  return {
    title: options?.genericTitle || 'Error',
    description: options?.genericDescription || error.error.message || 'An unexpected error occurred. Please try again.',
  };
};